import { useState, useRef, useMemo, useEffect } from "react"
import { useReactToPrint } from "react-to-print"
import BarcodeConfigComponent from "./barcode-config"
import { LabelLayoutSettingsValues } from "./types"
import { getDefaultLabelSettings } from "./barcodes-utils"
import { printerConfigToLabelSettings, type PrinterConfigApi } from "./printer-config-mappers"
import ManagePrinterConfigsDialog from "./ManagePrinterConfigsDialog"
import PrinterConfigModal, { type PrinterConfigModalMode } from "./PrinterConfigModal"
import { Alert, Box, Button, Container, Typography } from "@mui/material"
import { useParams, useSearchParams } from "react-router-dom"
import {
  FilterOperator,
  LogicalOperator,
  SortDirection,
  useGetProductLotsForBarcodesQuery,
  useGetPrinterConfigsQuery
} from "src/graphql/quark/quark.type"
import { CenteredBox, PageLoader, Stack } from "src/shared/components"
import { useTypedSelector } from "src/shared/store/store"
import InputAutocomplete from "src/shared/components/input-autocomplete"
// import { SettingsApplications as ManageIcon } from "@mui/icons-material"
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from "react-i18next"
export interface LotBarcodeT {
  actionId?: string | null
  batchExpiry?: string | null
  batchNumber?: string | null
  product: {
    name?: string | null
  }
}
// which product_lot column the :id route param filters on; no ?source= means grn_item_id
const SOURCE_FILTER_FIELD: Record<string, string> = {
  grn: "grn_item_id",
  "stock-operation": "stock_operation_item_id"
}

const Barcodes = () => {
  const { t } = useTranslation("invoices")
  const printRef = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const filterField =
    SOURCE_FILTER_FIELD[searchParams.get("source") ?? ""] ?? "grn_item_id"
  const locationId = useTypedSelector((state) => state.authentication.currentWhLoc?.id)

  const [config, setConfig] = useState<LabelLayoutSettingsValues>(getDefaultLabelSettings())
  const [selectedPrinterConfigId, setSelectedPrinterConfigId] = useState<string | null>(null)
  const [manageConfigsOpen, setManageConfigsOpen] = useState(false)
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveModalMode, setSaveModalMode] = useState<PrinterConfigModalMode>("create")
  const [editConfig, setEditConfig] = useState<PrinterConfigApi | null>(null)
  const defaultAppliedRef = useRef(false)

  const { data: printerConfigsData, loading: printerConfigsLoading, refetch: refetchPrinterConfigs } = useGetPrinterConfigsQuery({
    variables: { page: 1, limit: 100, locationId: locationId ?? undefined },
    skip: !locationId
  })

  const printerConfigs: PrinterConfigApi[] = useMemo(
    () => printerConfigsData?.getPrinterConfigs?.data ?? [],
    [printerConfigsData?.getPrinterConfigs?.data]
  )

  useEffect(() => {
    if (printerConfigsLoading || defaultAppliedRef.current || !printerConfigs.length) return
    const defaultConfig = printerConfigs.find((c) => c.isDefault)
    if (defaultConfig) {
      defaultAppliedRef.current = true
      setConfig(printerConfigToLabelSettings(defaultConfig))
      setSelectedPrinterConfigId(defaultConfig.id)
    }
  }, [printerConfigs, printerConfigsLoading])

  const handleUseConfig = (api: PrinterConfigApi) => {
    setConfig(printerConfigToLabelSettings(api))
    setSelectedPrinterConfigId(api.id)
    setManageConfigsOpen(false)
  }

  const handleEditConfig = (api: PrinterConfigApi) => {
    setConfig(printerConfigToLabelSettings(api))
    setSelectedPrinterConfigId(api.id)
    setEditConfig(api)
    setManageConfigsOpen(false)
    setSaveModalMode("edit")
    setSaveModalOpen(true)
  }

  const handleAddNewConfig = () => {
    setEditConfig(null)
    setSaveModalMode("create")
    setManageConfigsOpen(false)
    setSaveModalOpen(true)
  }

  const { data, loading } = useGetProductLotsForBarcodesQuery({
    variables: {
      locationId: locationId,
      page: 1,
      limit: 100000,
      sort: [{ field: "productLot.actionId", direction: SortDirection.Asc }],
      filter: {
        logicalOperator: LogicalOperator.And,
        conditions: [
          {
            value: id,
            operator: FilterOperator.Equal,
            field: filterField
          }
        ]
      }
    }
  })
  const lotBarcodes: LotBarcodeT[] = useMemo(() => {
    if (data?.getProductLots?.length) {
      return data.getProductLots
    }
    return []
    //  else if (!!actionId) {
    //   return [
    //     {
    //       actionId,
    //       batchExpiry: expiry,
    //       batchNumber: batch,
    //       product: {
    //         name: productName
    //       }
    //     }
    //   ]
    // } else {
    //   return []
    // }
  }, [data?.getProductLots])

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Barcodes - ${lotBarcodes?.[0]?.product?.name}`,
    pageStyle: `
      @page {
        margin: 0mm;
        size: auto;
      }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          height: auto !important;
        }
        body * {
          box-sizing: border-box !important;
        }
        .single-printable-content {
          margin: 0 !important;
          padding: 0 !important;
        }
        }
        `
  })

  if (loading) {
    return <PageLoader isLoading />
  }
  if (!lotBarcodes?.length) {
    return (
      <CenteredBox height={"100%"}>
        <Alert variant="filled" color="error">
          {t("barcodes.noBarcodesFound")}
        </Alert>
      </CenteredBox>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3, fontFamily: "OcrB" }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="700" color="primary" gutterBottom>
          {t("barcodes.title")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t("barcodes.subtitle")}
        </Typography>
        <Stack direction="row" alignItems={"flex-end"} spacing={2} >
          <InputAutocomplete
            label={t("barcodes.printerConfig")}
            placeholder={t("barcodes.printerConfigPlaceholder")}
            value={
              selectedPrinterConfigId
                ? (() => {
                  const c = printerConfigs.find((c) => c.id === selectedPrinterConfigId)
                  return c
                    ? { value: c.id, label: c.isDefault ? `${c.name} ${t("barcodes.default")}` : c.name }
                    : { value: "", label: t("barcodes.noneCustom") }
                })()
                : { value: "", label: t("barcodes.noneCustom") }
            }
            onChange={(val: any) => {
              const option = Array.isArray(val) ? val[0] : val
              if (!option || option.value === "") {
                setSelectedPrinterConfigId(null)
                return
              }
              const c = printerConfigs.find((x) => x.id === option.value)
              if (c) {
                setConfig(printerConfigToLabelSettings(c))
                setSelectedPrinterConfigId(c.id)
              }
            }}
            options={[
              { value: "", label: t("barcodes.noneCustom") },
              ...printerConfigs.map((c) => ({
                value: c.id,
                label: c.isDefault ? `${c.name} ${t("barcodes.default")}` : c.name
              }))
            ]}
            getOptionLabel={(opt: any) => opt?.label ?? ""}
            fullWidth
          // sx={{ minWidth: 260 }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setManageConfigsOpen(true)}
          >
            <SettingsIcon sx={{ color: theme => theme.palette.primary.main, pr: 0.5 }} /> {t("barcodes.configs")}
          </Button>
          {/* <Button
            variant="outlined"
            startIcon={<ManageIcon />}
          >
            Manage configs
          </Button> */}
        </Stack>
      </Box>

      {/* Configuration Panel */}
      <BarcodeConfigComponent
        ref={printRef}
        onConfigChange={(c) => {
          setConfig(c)
          setSelectedPrinterConfigId(null)
        }}
        config={config}
        lotBarcodes={lotBarcodes}
        onPrint={handlePrint}
        onSaveConfig={() => {
          setEditConfig(null)
          setSaveModalMode("create")
          setSaveModalOpen(true)
        }}
      />

      <ManagePrinterConfigsDialog
        open={manageConfigsOpen}
        onClose={() => setManageConfigsOpen(false)}
        configs={printerConfigs}
        loading={printerConfigsLoading}
        onRefetch={refetchPrinterConfigs}
        onUse={handleUseConfig}
        onEdit={handleEditConfig}
        onAddNew={handleAddNewConfig}
      />

      <PrinterConfigModal
        open={saveModalOpen}
        onClose={() => { setSaveModalOpen(false); setEditConfig(null) }}
        mode={saveModalMode}
        config={config}
        editId={editConfig?.id}
        editName={editConfig?.name}
        editIsDefault={editConfig?.isDefault ?? false}
        onSuccess={() => {
          refetchPrinterConfigs()
          setSaveModalOpen(false)
          setEditConfig(null)
        }}
      />
    </Container>
  )
}

export default Barcodes

// const printStyles = {
//   single: ``,
//   double: ``,
//   triple: `
//   @page {
//     margin: 0mm;
//     size: 106mm 24mm;
//     width: 106mm;
//     font-family:'OcrB';
//     height: 24mm;
//   }
//   @media print {
//     * {
//       margin: 0 !important;
//       padding: 0 !important;
//       box-sizing: border-box !important;
//     }
//     body {
//       -webkit-print-color-adjust: exact;
//       print-color-adjust: exact;
//       margin: 0 !important;
//       padding: 0 !important;
//       width: 106mm !important;
//       height: 24mm !important;
//     }
//     .printable-content {
//       background: white !important;
//       width: 106mm !important;
//       height: 24mm !important;
//       margin: 0 !important;
//       padding: 0 !important;
//     }
//   }`
// }

// .single-printable-content {
//   background: red !important;
//   width: 50mm !important;
//   height:  25mm !important;
//   margin: 0 !important;
//   padding: 0 !important;
// }
// body {
//   -webkit-print-color-adjust: exact;
//   print-color-adjust: exact;
//   margin: 0 !important;
//   padding: 0 !important;
//   width: 50mm !important;
//   height: 25mm !important;
// }
// @page {
//   margin: 0mm;
//   size: 50mm 25mm;
//   width: 50mm;
//   font-family:'OcrB';
//   height: 25mm;
// }
// @media print {
//   * {
//     margin: 0 !important;
//     padding: 0 !important;
//     box-sizing: border-box !important;
//   }
// }
